import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, getUserId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COLORS, VERSO_URL } from "@/data/constants";
import { toast } from "sonner";
import { Lock, Sparkles, Shuffle, X, Check, CreditCard, ArrowRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ILE_BOOK_ID = "jeu-ile-de-labondance"; // clé de reprise post-connexion pour Le Jeu

const C_COLOR = { conscience: COLORS.or, corps: COLORS.vert, coeur: COLORS.rouge, cerveau: COLORS.bleu };
const C_LABEL = { conscience: "CONSCIENCE", corps: "CORPS", coeur: "CŒUR", cerveau: "CERVEAU" };

const imgUrl = (card) => (card?.image_url ? `${BACKEND}${card.image_url}` : null);

export default function IleAbondance() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const authenticated = !!user && user !== false;

  const [cards, setCards] = useState([]);
  const [entitled, setEntitled] = useState(false);
  const [mode, setMode] = useState("jours"); // "jours" | "libre"
  const [responses, setResponses] = useState({});
  const [active, setActive] = useState(null); // active card object
  const [flipped, setFlipped] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [showUnlock, setShowUnlock] = useState(false);

  const load = () =>
    api.get("/game/ile/cards").then((r) => {
      setCards(r.data.cards || []);
      setEntitled(!!r.data.entitled);
    });

  useEffect(() => {
    load();
    api.get(`/game/ile/responses/${getUserId()}`).then((r) => setResponses(r.data || {})).catch(() => {});
  }, [user]);

  const accessibleCards = useMemo(() => cards.filter((c) => c.accessible), [cards]);

  const openCard = (card) => {
    if (!card.accessible) {
      setShowUnlock(true);
      return;
    }
    setActive(card);
    setDraft(responses[String(card.number)] || "");
    setFlipped(false);
    setTimeout(() => setFlipped(true), 260);
  };

  const drawRandom = () => {
    if (!entitled) {
      setShowUnlock(true);
      return;
    }
    const pool = accessibleCards;
    if (!pool.length) return;
    openCard(pool[Math.floor(Math.random() * pool.length)]);
  };

  const closeModal = () => {
    setActive(null);
    setFlipped(false);
    setDraft("");
  };

  const saveResponse = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.post("/game/ile/response", { user_id: getUserId(), number: active.number, text: draft });
      setResponses((r) => ({ ...r, [String(active.number)]: draft }));
      toast.success("Réponse enregistrée");
    } catch {
      toast.error("Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const startCheckout = async () => {
    if (!authenticated) {
      sessionStorage.setItem("tao_unlock_book", ILE_BOOK_ID);
      navigate(`/connexion?next=/jeu/ile`);
      return;
    }
    sessionStorage.setItem("tao_unlock_book", ILE_BOOK_ID);
    try {
      const { data } = await api.post("/payments/checkout", { product: "jeu", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      if (err.response?.status === 400) { toast.success("Jeu déjà débloqué"); load(); setShowUnlock(false); }
      else toast.error("Impossible de démarrer le paiement");
    }
  };

  const startPaypal = async () => {
    if (!authenticated) {
      sessionStorage.setItem("tao_unlock_book", ILE_BOOK_ID);
      navigate(`/connexion?next=/jeu/ile`);
      return;
    }
    sessionStorage.setItem("tao_unlock_book", ILE_BOOK_ID);
    try {
      const { data } = await api.post("/paypal/create-order", { product: "jeu", book_id: "jeu-ile-de-labondance", origin_url: window.location.origin });
      if (data.approve_url) window.location.href = data.approve_url;
      else toast.error("PayPal indisponible");
    } catch (err) {
      if (err.response?.status === 400) { toast.success("Jeu déjà débloqué"); load(); setShowUnlock(false); }
      else toast.error("PayPal indisponible pour le moment");
    }
  };

  return (
    <div className="bg-[#121814] min-h-screen">
      {/* Hero */}
      <section data-testid="ile-hero" className="relative overflow-hidden bg-[#1F2A24] text-[#F6F2E9]">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${VERSO_URL})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(6px)" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1F2A24]/70 via-[#1F2A24]/85 to-[#121814]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-8 py-16 md:py-24 text-center">
          <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C9A24B] font-semibold">Le Jeu numérique</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3">L'Île de l'Abondance</h1>
          <p className="font-serif italic text-base md:text-lg text-[#F6F2E9]/75 mt-5 max-w-2xl mx-auto leading-relaxed">
            21 cartes. Une par jour, ou au gré de vos tirages. Chaque carte ouvre une scène, une question, une action.
          </p>
          <div className="gold-divider w-24 mx-auto my-8" />
          {/* Mode toggle */}
          <div className="inline-flex rounded-full border border-[#C9A24B]/40 p-1 bg-black/20">
            <button
              data-testid="mode-jours"
              onClick={() => setMode("jours")}
              className={`rounded-full px-6 py-2.5 font-ui text-sm transition-colors ${mode === "jours" ? "bg-[#C9A24B] text-[#1F2A24]" : "text-[#F6F2E9]/80"}`}
            >
              Mode 21 jours
            </button>
            <button
              data-testid="mode-libre"
              onClick={() => (entitled ? setMode("libre") : setShowUnlock(true))}
              className={`rounded-full px-6 py-2.5 font-ui text-sm transition-colors inline-flex items-center gap-2 ${mode === "libre" ? "bg-[#C9A24B] text-[#1F2A24]" : "text-[#F6F2E9]/80"}`}
            >
              {!entitled && <Lock size={13} />} Tirage libre
            </button>
          </div>
          {mode === "libre" && entitled && (
            <div className="mt-8">
              <button
                data-testid="ile-draw-btn"
                onClick={drawRandom}
                className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-8 py-4 font-ui text-sm tracking-wide hover:bg-[#b8923f] transition-colors"
              >
                <Shuffle size={18} /> TIRER UNE CARTE
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Collection */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="font-sans text-xs uppercase tracking-[0.25em] text-[#C9A24B] font-semibold">La collection</p>
            <h2 className="font-serif text-2xl md:text-3xl text-[#F6F2E9] mt-1">Les 21 cartes</h2>
          </div>
          {!entitled && (
            <span className="font-sans text-xs text-[#F6F2E9]/60">Cartes 1-3 offertes · 4-21 premium</span>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5" data-testid="ile-collection">
          {cards.map((card) => {
            const answered = !!responses[String(card.number)];
            return (
              <button
                key={card.number}
                data-testid={`ile-card-${card.number}`}
                onClick={() => openCard(card)}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden border border-[#C9A24B]/20 bg-[#1F2A24] transition-transform duration-300 hover:-translate-y-1 hover:border-[#C9A24B]/60"
              >
                <img src={VERSO_URL} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/30" />
                <div className="absolute inset-0 flex flex-col items-center justify-between p-3 text-center">
                  <span className="font-serif text-lg text-[#C9A24B] drop-shadow mt-1">{String(card.number).padStart(2, "0")}</span>
                  {!card.accessible ? (
                    <div data-testid={`ile-card-lock-${card.number}`} className="rounded-full bg-black/55 backdrop-blur px-3 py-1.5 inline-flex items-center gap-1.5">
                      <Lock size={12} className="text-[#C9A24B]" />
                      <span className="font-ui text-[10px] tracking-wide text-white">Premium</span>
                    </div>
                  ) : (
                    <span className="rounded-full bg-white/20 backdrop-blur px-3 py-1 font-ui text-[10px] tracking-wide text-white">Découvrir</span>
                  )}
                  <span className="font-serif text-sm text-white/90 drop-shadow leading-tight pb-1">{card.title}</span>
                </div>
                {answered && (
                  <span className="absolute top-2 right-2 rounded-full bg-[#385E3A] p-1"><Check size={12} className="text-white" /></span>
                )}
              </button>
            );
          })}
        </div>

        {!entitled && (
          <div className="mt-12 text-center rounded-2xl border border-[#C9A24B]/30 bg-[#1F2A24] p-8 md:p-10">
            <Sparkles className="mx-auto text-[#C9A24B]" size={28} />
            <p className="font-serif text-xl md:text-2xl text-[#F6F2E9] mt-4">Débloquez les 21 cartes</p>
            <p className="font-sans text-sm text-[#F6F2E9]/70 mt-2 max-w-md mx-auto">
              Accédez à l'intégralité du Jeu et au tirage libre. Un seul déblocage, à vie.
            </p>
            <button
              data-testid="ile-unlock-cta"
              onClick={() => setShowUnlock(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-8 py-3.5 font-ui text-sm tracking-wide mt-6 hover:bg-[#b8923f] transition-colors"
            >
              DÉBLOQUER LE JEU
            </button>
          </div>
        )}
      </section>

      {/* Passerelle vers le Carnet */}
      <section className="max-w-4xl mx-auto px-4 sm:px-8 pb-16">
        <div data-testid="bridge-jeu-carnet" className="rounded-2xl border border-[#C9A24B]/30 bg-[#1F2A24] p-7 md:p-8 text-center">
          <p className="font-serif italic text-lg text-[#F6F2E9]/85">Vous souhaitez approfondir votre expérience pendant 21 jours ?</p>
          <Link
            to="/univers/ile-de-labondance"
            data-testid="bridge-to-carnet"
            className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-6 py-3 font-ui text-sm mt-4 hover:bg-[#b8923f] transition-colors"
          >
            Découvrir le Carnet <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Card recto modal */}
      {active && (
        <div data-testid="ile-modal" className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/80 backdrop-blur-sm overflow-y-auto p-4" onClick={closeModal}>
          <div className="relative w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
            <button data-testid="ile-close-modal" onClick={closeModal} className="absolute -top-2 -right-2 z-10 rounded-full bg-white/90 p-2 hover:bg-white transition-colors">
              <X size={18} className="text-[#1F2A24]" />
            </button>
            <div className="grid md:grid-cols-2 gap-0 rounded-3xl overflow-hidden bg-[#F6F2E9] shadow-2xl">
              {/* Illustration with flip */}
              <div className={`flip-card ${flipped ? "is-flipped" : ""} aspect-[3/4] md:aspect-auto md:min-h-[520px] bg-[#1F2A24]`}>
                <div className="flip-inner">
                  <div className="flip-face">
                    <img src={VERSO_URL} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flip-face flip-back bg-[#1F2A24]">
                    <img
                      data-testid="ile-modal-illustration"
                      src={imgUrl(active)}
                      alt={active.title}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              {/* Recto content */}
              <div className="p-6 md:p-8 flex flex-col">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-3xl text-[#C9A24B]">{String(active.number).padStart(2, "0")}</span>
                  <span
                    className="rounded-full px-3 py-1 font-ui text-[11px] tracking-widest text-white"
                    style={{ backgroundColor: C_COLOR[active.C_focus] || COLORS.vert }}
                    data-testid="ile-modal-dimension"
                  >
                    {C_LABEL[active.C_focus] || (active.C_focus || "").toUpperCase()}
                  </span>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[#1F2A24] mt-2">{active.title}</h3>
                {active.scene && <p className="font-serif italic text-sm text-[#1F2A24]/60 mt-2">{active.scene}</p>}

                <div className="mt-5">
                  <p className="font-ui text-[11px] tracking-widest" style={{ color: C_COLOR[active.C_focus] || COLORS.vert }}>LA QUESTION</p>
                  <p data-testid="ile-modal-question" className="font-serif text-lg text-[#1F2A24] mt-1 leading-snug">{active.question}</p>
                </div>
                <div className="mt-4">
                  <p className="font-ui text-[11px] tracking-widest" style={{ color: C_COLOR[active.C_focus] || COLORS.vert }}>L'ACTION</p>
                  <p data-testid="ile-modal-action" className="font-sans text-sm text-[#1F2A24]/80 mt-1">{active.action}</p>
                </div>

                <div className="mt-5 flex-1 flex flex-col">
                  <label className="font-ui text-[11px] tracking-widest text-[#1F2A24]/60">VOTRE RÉPONSE</label>
                  <textarea
                    data-testid="ile-response-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Écrivez ce qui vous vient…"
                    className="mt-2 w-full flex-1 min-h-[110px] rounded-xl border border-[#E8DCC4] bg-white p-3 font-sans text-sm text-[#1F2A24] focus:outline-none focus:border-[#C9A24B] resize-none"
                  />
                  <button
                    data-testid="ile-save-response"
                    onClick={saveResponse}
                    disabled={saving}
                    className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#385E3A] text-white px-6 py-3 font-ui text-sm hover:bg-[#2c4a2e] transition-colors disabled:opacity-50"
                  >
                    <Check size={16} /> {saving ? "Enregistrement…" : "Enregistrer ma réponse"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlock modal */}
      {showUnlock && (
        <div data-testid="ile-unlock-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setShowUnlock(false)}>
          <div className="relative w-full max-w-md rounded-3xl bg-[#F6F2E9] p-7 md:p-8 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button data-testid="ile-unlock-close" onClick={() => setShowUnlock(false)} className="absolute top-4 right-4 text-[#1F2A24]/50 hover:text-[#1F2A24]">
              <X size={20} />
            </button>
            <Sparkles className="mx-auto text-[#C9A24B]" size={30} />
            <h3 className="font-serif text-2xl text-[#1F2A24] mt-3">Débloquer L'Île de l'Abondance</h3>
            <p className="font-sans text-sm text-[#1F2A24]/70 mt-2">
              Les 21 cartes + le tirage libre. Un seul paiement, accès à vie.
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <button data-testid="ile-unlock-stripe" onClick={startCheckout} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm font-semibold hover:bg-[#b8923f] transition-colors">
                <CreditCard size={16} /> Payer par carte
              </button>
              <button data-testid="ile-unlock-paypal" onClick={startPaypal} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#003087] text-white px-7 py-3.5 font-ui text-sm font-semibold hover:bg-[#00256a] transition-colors">
                PayPal
              </button>
            </div>
            {!authenticated && (
              <p className="font-sans text-xs text-[#1F2A24]/55 mt-4">Vous serez invité à vous connecter avant le paiement.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
